import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import type { OrgSettings } from "@backstory/protocol";
import { api, queryKeys } from "../../api";
import { authStore } from "../../auth/store";
import { OrgLayout } from "../../components/Shell";
import { Field, PageHeader, Switch, useToast } from "../../components/ui";
import { ErrorState, Skeleton } from "../../components/States";
import { SaveBar, settingsEqual } from "../../components/UnsavedChanges";

export function HipaaPage() {
  const { org: orgId } = useParams({ strict: false }) as { org: string };
  const qc = useQueryClient();
  const toast = useToast();
  const org = useQuery({ queryKey: queryKeys.org(orgId), queryFn: () => api.orgs.get(orgId) });
  const [s, setS] = useState<OrgSettings | null>(null);
  const [saved, setSaved] = useState<OrgSettings | null>(null);
  useEffect(() => {
    if (org.data) {
      setS(org.data.settings);
      setSaved(org.data.settings);
    }
  }, [org.data]);
  const draft = s ? { phi: s.phi, sessionIdleMinutes: s.sessionIdleMinutes, sessionAbsoluteHours: s.sessionAbsoluteHours } : null;
  const baseline = saved ? { phi: saved.phi, sessionIdleMinutes: saved.sessionIdleMinutes, sessionAbsoluteHours: saved.sessionAbsoluteHours } : null;
  const dirty = !!draft && !settingsEqual(draft, baseline);

  const save = useMutation({
    mutationFn: () =>
      api.orgs.patch(orgId, {
        settings: s ? { phi: s.phi, sessionIdleMinutes: s.sessionIdleMinutes, sessionAbsoluteHours: s.sessionAbsoluteHours } : undefined,
      }),
    onSuccess: () => {
      setSaved(s);
      void qc.invalidateQueries({ queryKey: queryKeys.org(orgId) });
      void authStore.load();
      toast.push("success", "HIPAA settings saved.");
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });

  return (
    <OrgLayout orgId={orgId}>
      <PageHeader title="HIPAA" />
      {org.isPending || !s ? (
        <Skeleton />
      ) : org.isError ? (
        <ErrorState error={org.error} retry={() => void org.refetch()} />
      ) : (
        <>
          <div className="card form-card">
            <section className="section">
            <div className="settings-stack">
              <Switch
                checked={s.phi}
                onChange={(v) => setS({ ...s, phi: v, sessionIdleMinutes: v && s.sessionIdleMinutes > 15 ? 15 : s.sessionIdleMinutes })}
                label="This organization processes protected health information"
                hint="Caps the idle timeout at 15 minutes. Masking follows the privacy option used when the session was recorded, and every session shows that mode."
              />
              <div className="field-row">
                <Field label="Idle timeout" hint={s.phi ? "Minutes. Capped at 15 for PHI." : "Minutes. Default 720."}>
                  {(p) => <input {...p} className="input" type="number" min={5} max={s.phi ? 15 : 1440} value={s.sessionIdleMinutes} onChange={(e) => setS({ ...s, sessionIdleMinutes: Number(e.target.value) })} />}
                </Field>
                <Field label="Absolute lifetime" hint="Hours. Default 168.">
                  {(p) => <input {...p} className="input" type="number" min={1} max={720} value={s.sessionAbsoluteHours} onChange={(e) => setS({ ...s, sessionAbsoluteHours: Number(e.target.value) })} />}
                </Field>
              </div>
            </div>
            </section>
          </div>
          <SaveBar dirty={dirty} saving={save.isPending} onSave={() => save.mutate()} onDiscard={() => saved && setS(saved)} />
        </>
      )}
    </OrgLayout>
  );
}
